import React, { useEffect, useRef } from 'react';
import { X, CreditCard, Calendar, User, Mail, Phone, MapPin, Download, RefreshCw, AlertCircle, CheckCircle, Clock, Building } from 'lucide-react';
import TransactionStatusBadge from './TransactionStatusBadge';

const TransactionDetailsModal = ({
  isOpen,
  onClose,
  transaction,
  onDownloadReceipt,
  onProcessRefund
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
      case 'declined':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const canRefund = (transaction) => {
    return transaction?.status === 'completed' &&
           new Date(transaction.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days
  };

  if (!isOpen || !transaction) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white shadow-2xl rounded-2xl transform transition-all duration-300 scale-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-teal-50">
            <div className="flex items-center gap-4 min-w-0">
              {getStatusIcon(transaction.status)}
              <div className="min-w-0">
                <h2 id="modal-title" className="text-2xl font-bold text-gray-900 truncate">
                  Transaction Details
                </h2>
                <p className="text-sm text-gray-600 mt-1 truncate">
                  Transaction #{transaction.transactionId} • {formatDate(transaction.date)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <TransactionStatusBadge status={transaction.status} size="large" />
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto overflow-x-hidden max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
              {/* Left Column - Transaction Details */}
              <div className="space-y-6 min-w-0">
                {/* Transaction Summary */}
                <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    Transaction Summary
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Transaction ID</label>
                        <p className="text-sm font-mono text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 truncate" title={transaction.transactionId}>
                          {transaction.transactionId}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Order ID</label>
                        <p className="text-sm font-mono text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 truncate" title={transaction.orderId || 'N/A'}>
                          {transaction.orderId || 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Amount</label>
                        <p className="text-lg font-bold text-teal-700 truncate">
                          ₹{transaction.amount.toFixed(2)}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Transaction Fees</label>
                        <p className="text-sm text-gray-900 truncate">
                          ₹{transaction.fees?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>

                    {transaction.refundAmount && (
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Refund Amount</label>
                        <p className="text-lg font-bold text-red-600 truncate">
                          ₹{transaction.refundAmount.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </section>

                {/* Payment Information */}
                <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    Payment Information
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Payment Method</label>
                        <p className="text-sm text-gray-900 capitalize truncate">
                          {transaction.paymentMethod?.replace('_', ' ') || 'Unknown'}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Gateway</label>
                        <p className="text-sm text-gray-900 truncate">
                          {transaction.gateway || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {transaction.paymentDetails && (
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Payment Details</label>
                        <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 break-words">
                          {transaction.paymentDetails}
                        </p>
                      </div>
                    )}

                    {transaction.gatewayTransactionId && (
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-gray-500">Gateway Transaction ID</label>
                        <p className="text-sm font-mono text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 break-all">
                          {transaction.gatewayTransactionId}
                        </p>
                      </div>
                    )}
                  </div>
                </section>

                {/* Timeline */}
                {transaction.timeline && (
                  <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      Transaction Timeline
                    </h3>

                    <div className="space-y-4">
                      {transaction.timeline.map((event, index) => {
                        const isLast = index === transaction.timeline.length - 1;

                        return (
                          <div key={index} className="flex gap-4">
                            <div className="flex flex-col items-center flex-shrink-0">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                                {getStatusIcon(event.status)}
                              </div>
                              {!isLast && (
                                <div className="w-0.5 h-8 bg-gray-200 mt-2"></div>
                              )}
                            </div>

                            <div className="flex-1 pb-8 min-w-0">
                              <div className="flex justify-between items-start gap-3">
                                <div className="min-w-0">
                                  <h4 className="font-medium text-gray-900 capitalize truncate">
                                    {event.status}
                                  </h4>
                                  <p className="text-sm text-gray-600 mt-1 break-words">
                                    {event.description}
                                  </p>
                                </div>
                                <div className="text-right text-sm text-gray-500 flex-shrink-0 whitespace-nowrap">
                                  <div>{formatDate(event.date)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>

              {/* Right Column - Customer & Order Info */}
              <div className="space-y-6 min-w-0">
                {/* Customer Information */}
                <section className="bg-teal-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    Customer Information
                  </h3>

                  <div className="flex items-center gap-4 mb-4 min-w-0">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">
                        {transaction.customer?.name || 'Unknown Customer'}
                      </h4>
                      <p className="text-sm text-gray-600 truncate">
                        Customer ID: {transaction.customer?.id || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-700 truncate">
                        {transaction.customer?.email || 'No email provided'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-700 truncate">
                        {transaction.customer?.phone || 'No phone provided'}
                      </span>
                    </div>
                  </div>
                </section>

                {/* Billing Address */}
                {transaction.billingAddress && (
                  <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      Billing Address
                    </h3>

                    <div className="text-sm text-gray-700 space-y-1 break-words">
                      <div className="font-medium break-words">
                        {transaction.billingAddress.name || transaction.customer?.name}
                      </div>
                      <div className="break-words">{transaction.billingAddress.street}</div>
                      <div className="break-words">
                        {transaction.billingAddress.city}, {transaction.billingAddress.state} {transaction.billingAddress.zipCode}
                      </div>
                      <div className="break-words">{transaction.billingAddress.country}</div>
                    </div>
                  </section>
                )}

                {/* Order Items */}
                {transaction.orderItems && (
                  <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Order Items ({transaction.orderItems.length})
                    </h3>

                    <div className="space-y-3">
                      {transaction.orderItems.map((item, index) => (
                        <div key={index} className="flex gap-3 p-3 bg-white rounded-lg border border-gray-200 min-w-0">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm truncate" title={item.name}>
                              {item.name}
                            </h4>
                            <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                          </div>
                          <div className="text-sm font-medium text-gray-900 flex-shrink-0 whitespace-nowrap">
                            ₹{(item.price * item.quantity).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Error Information */}
                {transaction.status === 'failed' && transaction.errorMessage && (
                  <section className="bg-red-50 rounded-xl p-6 border border-red-200">
                    <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      Error Information
                    </h3>
                    <p className="text-sm text-red-700 break-words">{transaction.errorMessage}</p>
                    {transaction.errorCode && (
                      <p className="text-sm text-red-600 mt-2 break-words">
                        Error Code: {transaction.errorCode}
                      </p>
                    )}
                  </section>
                )}

                {/* Action Buttons */}
                <section className="space-y-3">
                  {transaction.status === 'completed' && (
                    <button
                      onClick={() => {
                        onDownloadReceipt(transaction);
                        onClose();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium"
                    >
                      <Download className="w-5 h-5" />
                      Download Receipt
                    </button>
                  )}

                  {canRefund(transaction) && (
                    <button
                      onClick={() => {
                        onProcessRefund(transaction);
                        onClose();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 font-medium"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Process Refund
                    </button>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionDetailsModal;